import { WebMercator } from "../../map/projection.js";
import { createHTMLElement } from "../../util/dom-util.js";
/**
 * Extend this class and override the essential methods in order to
 * implement a custom tile layer overlaying a map. The default (i.e. this implementation)
 * version will use DIV elements as the base of the tile.
 * First construct an instance of this class (or its derivatives) and call
 * addTo(map: L.Map) to make it visible on the given map. Call remove()
 * to remove it again.
 */
export class TileLayer {
    layer;
    tiles = new Map();
    coords = new Map();
    bounds = new Map();
    constructor(layer) {
        this.layer = layer;
    }
    /**
     * Call this method when it has to be removed and or needs to be destroyed
     */
    remove() {
        this.layer.remove();
    }
    ;
    /**
     * Call this method in order to add this layer to the given map.
     * @param map The leaflet map where to add this layer to.
     */
    addTo(map) {
        this.layer.addTo(map, this);
    }
    onReady(zoom, resolution, tileSize) {
        console.log(`loading tiles for zoom level ${this.layer.getZoom()} finished: count ${this.tiles.size}/${this.bounds.size}`);
    }
    /**
     * Gets called when a tile needs to be created.
     * @param coords the tile coordinates
     * @param ownerDocument the document to use for element creation
     * @returns the created SVGSVGElement that represents this tile
     */
    createTile(coords, ownerDocument) {
        const key = `${coords.x}_${coords.y}_${coords.z}`;
        const bounds = tileBounds(coords, this.layer.getTileDimensions(), this.layer.getResolution());
        const tile = this.createTileElement(key, coords, this.layer.getTileDimensions(), bounds, ownerDocument);
        tile.style.width = `${this.layer.getTileDimensions().x}px`;
        tile.style.height = `${this.layer.getTileDimensions().y}px`;
        tile.style.overflow = "visible";
        tile.dataset["key"] = key;
        this.coords.set(key, coords);
        this.tiles.set(key, tile);
        this.bounds.set(key, bounds);
        const tileProperties = this.getTileProperties(key);
        this.initTileContent(tile, coords, tileProperties);
        this.onAddTile(tile, coords, this.layer.getResolution(), tileProperties);
        return tile;
    }
    removeTile(key) {
        const tileProperties = this.getTileProperties(key);
        const tile = this.tiles.get(key);
        this.tiles.delete(key);
        this.coords.delete(key);
        this.bounds.delete(key);
        this.onRemoveTile(tile, tileProperties.coords, tileProperties);
    }
    /**
     * Override this method.
     * @param elem The HTMLDivElement | SVGSVGElement for this tile
     * @param coords the tile coordinates
     * @param properties the SVGTileProperties
     * @returns undefined when all went well, otherwise an Error object (Promise<Error | undefined>).
     */
    async initTileContent(elem, coords, properties) {
        let error = undefined;
        return error;
    }
    createTileElement(key, coords, tileSize, bounds, ownerDocument) {
        return createHTMLElement("div", ownerDocument);
    }
    /**
     * Constructs  the tile properties for given key
     * @param key the tile key
     * @returns the (SVG)TileProperties
     */
    getTileProperties(key) {
        let coords = this.coords.get(key);
        let bounds = this.bounds.get(key);
        coords = coords ?? parseKey(key);
        bounds = bounds ?? tileBounds(coords, this.layer.getTileDimensions(), this.layer.getResolution());
        return {
            key,
            size: {
                x: this.layer.getTileDimensions().x,
                y: this.layer.getTileDimensions().y,
            },
            coords,
            bounds,
        };
    }
    /**
     * Gets called when the tile has been added to the tile layer but before the call to onReady().
     * @param tile The SVGSVGElement for this tile
     * @param coords the tile coordinates
     * @param properties the tile properties
     */
    onAddTile(tile, coords, resolution, properties) {
        console.log(`tile added `, coords, `count ${this.tiles.size}/${this.bounds.size}`);
    }
    /**
     * Gets called when the tile has been removed from the tile layer. These call is made
     * after the onReady() call.
     * @param tile The SVGSVGElement for this tile
     * @param coords the tile coordinates
     * @param properties the tile properties
     */
    onRemoveTile(tile, coords, properties) {
        console.log(`tile removed `, coords, `count ${this.tiles.size}/${this.bounds.size}`);
    }
    getTiles(expression) {
        let latLngExpressions;
        if (!expression.pop ||
            typeof expression[0] === "number") {
            latLngExpressions = [expression];
        }
        else {
            latLngExpressions = expression;
        }
        const tiles = Array.from(this.bounds.entries())
            .filter(([key, bounds]) => latLngExpressions.reduce((inside, latLng) => inside || contains(bounds, latLng), false))
            .map(([key, bounds]) => {
            return this.getTileProperties(key);
        });
        return tiles;
    }
    getTileElements(key) {
        return (key.toLowerCase ? [key] : key).map((tileKey) => this.tiles.get(tileKey));
    }
}
///////////////////////////////////////
// helper functions ///////////////////
///////////////////////////////////////
const tileBounds = (coords, tileSize, resolution) => {
    const { x, y, z } = coords;
    const bl = { x: x * tileSize.x, y: (y + 1) * tileSize.y };
    const tr = { x: (x + 1) * tileSize.x, y: y * tileSize.y };
    return {
        southWest: WebMercator.pointToLatLng(bl, resolution),
        northEast: WebMercator.pointToLatLng(tr, resolution),
    };
};
const contains = (bounds, latLng) => {
    return (latLng.lat <= bounds.northEast.lat &&
        latLng.lat > bounds.southWest.lat &&
        latLng.lng < bounds.northEast.lng &&
        latLng.lng >= bounds.southWest.lng);
};
const parseKey = (key) => {
    let x = 0, y = 0, z = 0;
    if (key) {
        const i0 = key.indexOf("_");
        const i1 = key.indexOf("_", i0 + 1);
        if (i0 > i1) {
            x = Number.parseInt(key.substring(0, i0));
            y = Number.parseInt(key.substring(i0 + 1, i1));
            z = Number.parseInt(key.substring(i1 + 1));
        }
    }
    return { x, y, z };
};
